import { useMemo } from "react";
import type { User } from "@/api/auth";
import type { AdminAction } from "./types";

/**
 * Client-side permission matrix — MIRROR backend `_ACTION_MATRIX` ở
 * `apps/accounts/permissions.py`.
 *
 * Sprint 8A B1: hard-code inline thay vì fetch để khỏi chờ B2 endpoint.
 * Sprint 8A B2+: switch sang fetch /api/admin/me/permissions/ via TanStack Query,
 * BE là source of truth, FE chỉ cache.
 *
 * Sync rules với backend (KHI BE thay đổi matrix, BẮT BUỘC update file này):
 *   - student: []
 *   - instructor: course.edit/delete/publish (course ĐANG được phân công) + lesson/quiz CRUD
 *     — KHÔNG có course.create/course.assign_instructor (chốt 2026-07-13)
 *   - tenant_admin: instructor + course.create/course.assign_instructor + user/order/audit/analytics
 *     (KHÔNG có role_change)
 *   - superadmin: ALL (wildcard)
 */

const INSTRUCTOR_ACTIONS: AdminAction[] = [
  // Chốt 2026-07-13: KHÔNG có "course.create" — chỉ tenant_admin/superadmin
  // tạo course, instructor chỉ edit course ĐANG được phân công phụ trách.
  "course.edit",
  "course.delete",
  "course.publish",
  "lesson.create",
  "lesson.edit",
  "lesson.delete",
  "lesson.reorder",
  "quiz.create",
  "quiz.edit",
  "quiz.delete",
  "question.create",
  "question.edit",
  "question.delete",
  // Sprint 30 — chấm bài short_answer/essay (grading queue + AI gợi ý)
  "answer.grade",
  // Plan 2026-10-03 — thông báo cho môn mình phụ trách + kiểm duyệt diễn đàn môn mình.
  // Mirror `_ACTION_MATRIX` (backend/apps/accounts/permissions.py).
  "announcement.send",
  "forum.moderate",
  "rubric.manage",
  "appeal.review",
];

const TENANT_ADMIN_ACTIONS: AdminAction[] = [
  ...INSTRUCTOR_ACTIONS,
  // Chốt 2026-07-13 — tenant_admin (KHÔNG kế thừa từ instructor nữa, đứng
  // riêng ở đây): tạo course + phân công/đổi/gỡ giảng viên phụ trách.
  "course.create",
  "course.assign_instructor",
  // Plan 2026-10-27 — nạp gói nội dung F-Future (.zip) — mirror `_ACTION_MATRIX`.
  "course.import_pack",
  "user.create",
  "user.edit",
  "user.delete",
  "user.invite",
  "user.export",
  "user.bulk_import",
  "major.assign",
  "major.edit",
  "major.remove",
  "term_offering.manage",
  "curriculum.manage",
  "order.read",
  "order.refund",
  "audit.read",
  "analytics.read",
  // Sprint 30 — cấu hình per-tenant AI-assist grading mode (auto/on_demand)
  "essay_grading.settings",
  // Sprint 26+ — cấu hình lịch sử học phí/tín chỉ theo tenant
  "tuition_rate.manage",
];

const SUPERADMIN_ACTIONS: AdminAction[] = [...TENANT_ADMIN_ACTIONS, "user.role_change"];

const ROLE_ACTIONS: Record<string, AdminAction[]> = {
  student: [],
  instructor: INSTRUCTOR_ACTIONS,
  tenant_admin: TENANT_ADMIN_ACTIONS,
  superadmin: SUPERADMIN_ACTIONS,
};

export interface PermissionsApi {
  actions: AdminAction[];
  hasAction: (action: AdminAction) => boolean;
}

/**
 * Trả về { actions, hasAction(action) } theo role của user.
 * Memoized — chỉ recompute khi user.role đổi.
 */
export function usePermissions(user: { role: string } | null | undefined): PermissionsApi {
  return useMemo(() => {
    const actions = user ? (ROLE_ACTIONS[user.role] ?? []) : [];
    const set = new Set(actions);
    return {
      actions,
      hasAction: (action: AdminAction) => set.has(action),
    };
  }, [user?.role]);
}

/**
 * Standalone helper — dùng cho non-React context (vd guard logic).
 */
export function hasAction(
  user: Pick<User, "role"> | null | undefined,
  action: AdminAction,
): boolean {
  if (!user) return false;
  return (ROLE_ACTIONS[user.role] ?? []).includes(action);
}
